// components/CollapsibleSection.tsx
import React, { useState, useCallback, createContext, useContext } from 'react';

// Context for child facets to register their selectAll/clearAll actions
interface CollapsibleActionsContextType {
  registerActions: (actions: { selectAll?: () => void; clearAll?: () => void; optionCount?: number }) => void;
}

export const CollapsibleActionsContext = createContext<CollapsibleActionsContextType | null>(null);

export const useCollapsibleActions = () => useContext(CollapsibleActionsContext);

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  actions?: React.ReactNode;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultExpanded = false,
  actions,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [registeredActions, setRegisteredActions] = useState<{
    selectAll?: () => void;
    clearAll?: () => void;
    optionCount?: number;
  }>({});

  const registerActions = useCallback((actions: { selectAll?: () => void; clearAll?: () => void; optionCount?: number }) => {
    setRegisteredActions(actions);
  }, []);

  const showAllNone = isExpanded && registeredActions.selectAll && registeredActions.clearAll && (registeredActions.optionCount === undefined || registeredActions.optionCount > 3);

  return (
    <CollapsibleActionsContext.Provider value={{ registerActions }}>
      <div className="group">
        <div
          className="flex items-center w-full border-t border-mp-border pt-3 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="font-display text-xs uppercase tracking-widest text-mp-muted flex-1">
            {title}
          </span>
          <div className="flex items-center gap-3">
            {isExpanded && actions && (
              <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                {actions}
              </div>
            )}
            {showAllNone && (
              <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                <button
                  onClick={registeredActions.selectAll}
                  className="text-mp-muted font-mono text-[10px] uppercase hover:text-mp-text transition-colors"
                >
                  All
                </button>
                <button
                  onClick={registeredActions.clearAll}
                  className="text-mp-muted font-mono text-[10px] uppercase hover:text-mp-text transition-colors"
                >
                  None
                </button>
              </div>
            )}
            <span className="material-symbols-outlined text-mp-muted text-[18px] opacity-0 group-hover:opacity-100 transition-opacity">
              {isExpanded ? 'expand_less' : 'expand_more'}
            </span>
          </div>
        </div>

        {isExpanded && <div className="mt-1">{children}</div>}
      </div>
    </CollapsibleActionsContext.Provider>
  );
};
